/* RESPONSIBLE TEAM: team-automation-foundations */
import {
  BaseConfig,
  INPUT_RULE_EMOJI,
  INPUT_RULE_MARKDOWN_ANCHOR,
  INPUT_RULE_ORDERED_LIST,
  INPUT_RULE_UNORDERED_LIST,
} from '@intercom/composer-ember';
import {
  INLINE_CONTROL_ALIGNMENT,
  type TypeaheadConfig,
  type TextDirection,
} from '@intercom/composer-ember/lib/config/composer-config';
import { EMOJI_TYPEAHEAD } from '@intercom/composer-ember';
import EmbercomFileUploader from 'embercom/lib/articles/embercom-file-uploader';
import { EmbercomCanvasNetworkClient } from 'embercom/objects/composer/embercom-canvas-network-client';
import type AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';
import { MENTION_TYPEAHEAD } from 'embercom/objects/inbox/composer-config';
import { type OutputParam } from 'embercom/components/workflows/graph-editor/node-items/steps/fin-custom';
import { type ComponentLike } from '@glint/template';
import type AttributeDescriptor from 'embercom/models/operator/visual-builder/attribute-descriptor';
import type Component from '@glimmer/component';

export default class ComposerConfig extends BaseConfig {
  autoFocus = true;
  allowedBlocks = [
    'paragraph',
    'heading',
    'subheading',
    'image',
    'video',
    'videoFile',
    'audioFile',
    'orderedList',
    'unorderedList',
    'messengerCard',
    'mention',
  ];
  allowedInline = ['bold', 'italic', 'anchor'];
  hideFromTextFormatter = [INLINE_CONTROL_ALIGNMENT];
  allowTextAlignment = true;
  emojiInserter = 'composer/emoji-inserter';
  canvasNetworkClient;
  experimental = {
    hideInsertersOnMouseOut: true,
  };
  inputRules = [
    INPUT_RULE_EMOJI,
    INPUT_RULE_MARKDOWN_ANCHOR,
    INPUT_RULE_ORDERED_LIST,
    INPUT_RULE_UNORDERED_LIST,
  ];
  textDirection: TextDirection = 'auto';
  tools = [
    { name: 'text-formatter' },
    { name: 'template-inserter' },
    { name: 'anchor-editor', options: { showHelpLinkHeader: true } },
    { name: 'fallback-editor' },
    { name: 'image-editor', options: { supportAltAttributeEditing: false } },
    { name: 'video-editor' },
  ];
  typeaheads = [EMOJI_TYPEAHEAD];
  upload = {
    allowedImageFileTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'],
    allowedVideoFileTypes: ['video/mp4'],
    allowedAttachmentTypes: ['*'],
    uploader: EmbercomFileUploader,
    attrs: { policyUrl: '' },
    onUploadStart: () => {},
    onUploadFinish: () => {},
  };
  templating: { picker: string; resolver: AttributeInfoResolver };
  aiPromptData?: {
    onClickComponent: ComponentLike<unknown> | Component;
    onAddOutputParam: (param: OutputParam) => AttributeDescriptor;
    resolver: AttributeInfoResolver;
  };
  maxCharacterCount?: number;

  constructor({
    app,
    resolver,
    placeholder,
    allowMentions,
    hideTextFormatter,
    maxCharacterCount,
  }: {
    app: any;
    resolver: AttributeInfoResolver;
    placeholder: string;
    allowMentions?: boolean;
    hideTextFormatter?: boolean;
    maxCharacterCount?: number;
  }) {
    super();

    this.initializeMentions(!!allowMentions);

    this.canvasNetworkClient = new EmbercomCanvasNetworkClient(app.id);
    this.templating = { picker: 'common/attribute-picker', resolver };
    this.upload.attrs.policyUrl = `/apps/${app.id}/uploads`;
    this.placeholder = placeholder;
    this.tools = hideTextFormatter
      ? this.tools.filter((tool) => tool.name !== 'text-formatter')
      : this.tools;
    this.maxCharacterCount = maxCharacterCount;
  }

  removeAllowedBlock(block: string) {
    this.allowedBlocks = this.allowedBlocks.without(block);
  }

  insertTypeahead(typeahead: TypeaheadConfig) {
    this.typeaheads = [...this.typeaheads, typeahead];
  }

  private initializeMentions(allowMentions: boolean) {
    if (allowMentions) {
      this.insertTypeahead(MENTION_TYPEAHEAD);
    } else {
      this.removeAllowedBlock('mention');
    }
  }
}
